import { supabase } from './supabaseConfig'
import { event } from '@/utils/gtag'

// Browser and device detection utilities
const detectBrowser = () => {
    if (typeof window === 'undefined') return 'unknown';
    
    const userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.includes('safari') && !userAgent.includes('chrome')) return 'safari';
    if (userAgent.includes('chrome')) return 'chrome';
    if (userAgent.includes('firefox')) return 'firefox';
    if (userAgent.includes('edge')) return 'edge';
    return 'unknown';
};

const isMobileDevice = () => {
    if (typeof window === 'undefined') return false;
    return /iphone|ipad|ipod|android|blackberry|mini|windows\sce|palm/i.test(window.navigator.userAgent);
};

const isSafari = () => {
    if (typeof window === 'undefined') return false;
    const userAgent = window.navigator.userAgent.toLowerCase();
    return userAgent.includes('safari') && !userAgent.includes('chrome');
};

// Get canonical redirect URL based on environment
const getRedirectUrl = () => {
    // Server-side context: use environment variable with localhost fallback  
    if (typeof window === 'undefined') {
        // In SSR, process.env is available
        // eslint-disable-next-line no-undef
        const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || "http://localhost:3000";
        return `${baseUrl}/auth/callback`;
    }
    
    // Client-side context: use window.location.origin
    return `${window.location.origin}/auth/callback`;
};

// Telemetry logging
const logAuthEvent = (event, data = {}) => {
    console.log(`[AUTH_TELEMETRY] ${event}:`, {
        timestamp: new Date().toISOString(),
        browser: detectBrowser(),
        isMobile: isMobileDevice(),
        ...data
    });
    
    // TODO: Send to analytics service if needed
    // Example: analytics.track(event, data);
};

export const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
        email,
        password,
    })
    
    // Track signup attempt with user context
    if (!error && data.user) {
        event({
            action: 'sign_up',
            category: 'authentication',
            label: 'email',
            value: 1,
            userId: data.user.id,
            userEmail: data.user.email
        });
    }
    
    return { data, error }
}

export const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
    })
    
    // Track successful login with user context
    if (!error && data.user) {
        event({
            action: 'login',
            category: 'authentication',
            label: 'email',
            value: 1,
            userId: data.user.id,
            userEmail: data.user.email
        });
    }
    
    return { data, error }
}

export const signOut = async () => {
    logAuthEvent('logout_started');
    
    try {
        // Track logout before clearing session
        event({
            action: 'logout',
            category: 'authentication',
            label: 'manual',
            value: 1
        });
        
        // Clear Supabase auth
        const { error } = await supabase.auth.signOut();
        
        if (error) {
            console.error('Supabase signOut error:', error);
            return { error };
        }
        
        // Clear localStorage items
        if (typeof window !== 'undefined') {
            // Clear encryption key
            localStorage.removeItem('lyricboost-key');
            
            // Clear any Lyra greeting state
            localStorage.removeItem('lyraHasGreeted');
            
            // Clear any other app-specific local storage items
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith('lyricboost-')) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
            
            // Clear session storage
            sessionStorage.clear();
            
            // Clear service worker caches if they exist
            if ('serviceWorker' in navigator && 'caches' in window) {
                try {
                    const cacheNames = await caches.keys();
                    await Promise.all(
                        cacheNames.map(cacheName => {
                            if (cacheName.includes('lyric-boost')) {
                                return caches.delete(cacheName);
                            }
                        })
                    );
                } catch (cacheError) {
                    console.warn('Failed to clear service worker caches:', cacheError);
                }
            }
        }
        
        logAuthEvent('logout_complete');
        return { error: null };
        
    } catch (error) {
        console.error('Error during logout:', error);
        logAuthEvent('logout_error', { error: error.message });
        return { error };
    }
}

export const signInWithGoogle = async () => {
    const redirectUrl = getRedirectUrl();
    const browser = detectBrowser();
    const isMobile = isMobileDevice();
    const useSafari = isSafari();
    
    // Prefer redirect flow on mobile/Safari, popup on desktop
    const usePopup = !isMobile && !useSafari && browser === 'chrome';
    
    logAuthEvent('oauth_start', { 
        provider: 'google',
        flow: usePopup ? 'popup' : 'redirect',
        redirectUrl,
        browser
    });
    
    const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
            redirectTo: redirectUrl,
            // Force Google account chooser and request offline access
            queryParams: {
                prompt: 'select_account',
                access_type: 'offline'
            },
            // Use popup only on desktop Chrome for reliability
            ...(usePopup && { 
                skipBrowserRedirect: false  // Let Supabase handle the redirect even for "popup"
            })
        }
    });
    
    if (data) {
        logAuthEvent('oauth_initiated', { 
            provider: 'google',
            flow: usePopup ? 'popup' : 'redirect'
        });
        
        // Track Google OAuth attempt
        event({
            action: 'oauth_attempt',
            category: 'authentication',
            label: 'google',
            value: 1
        });
    }
    
    return { data, error }
}

export const signUpWithGoogle = async () => {
    // Use the same function as signInWithGoogle since OAuth handles both cases
    return await signInWithGoogle();
}